import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { tap } from 'rxjs/operators';

import { TEMPLATE_PREFIX } from '../../environments/locale-config';
import { TRANSLATE_SERVICE_TOKEN, TranslateService } from '../../services/common-injection-tokens';
import { FOLDER_SERVICE_TOKEN, FolderService, UnitFolder } from '../../services/folder.service';
import { ILocalizedToasterService, LOCALIZED_TOASTER_SERVICE_TOKEN } from '../../services/ILocalizedToasterService';

export enum CreateFolderInputTypes {
  Text = 'text',
  Number = 'number'
}

@UntilDestroy()
@Component({
    selector: 'kh-create-folder',
    templateUrl: TEMPLATE_PREFIX + 'create-folder.component.html',
    styleUrls: ['./create-folder.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreateFolderComponent implements OnInit {
  public folderId!: string;
  public folderName = 'New Folder';
  public isProcessing = false;
  public inputTypes = CreateFolderInputTypes;
  public folderCreatedText = '';
  public localizedNewFolderName = '';
  @Output() public pushFolder = new EventEmitter<UnitFolder>();
  @Input() public createFolderCallback?: (folderName: string) => void;

  public constructor(
    private readonly bsModalRef: BsModalRef,
    @Inject(FOLDER_SERVICE_TOKEN) private readonly folderService: FolderService,
    @Inject(LOCALIZED_TOASTER_SERVICE_TOKEN) private readonly toaster: ILocalizedToasterService,
    @Inject(TRANSLATE_SERVICE_TOKEN) private readonly translateService: TranslateService
  ) { }

  public ngOnInit(): void {
    this.localizedNewFolderName = this.translateService.instant('New folder name');
    const localizedDefault = this.translateService.instant('New Folder');
    this.folderName = localizedDefault;
  }

  public closeModal(): void {
    this.bsModalRef.hide();
  }

  public createFolder(): void {
    if (!this.folderName || this.folderName.trim() === '') {
      return;
    }

    this.isProcessing = true;

    if (this.createFolderCallback) {
      this.createFolderCallback(this.folderName.trim());
      this.isProcessing = false;
      this.bsModalRef.hide();
      return;
    }

    this.folderCreatedText = this.translateService.instant('Folder created!');

    this.folderService.createFolder(this.folderId, this.folderName)
      .pipe(
        tap((folderInfo: UnitFolder) => {
          this.pushFolder.emit(folderInfo);
          this.toaster.success(this.folderCreatedText);
          this.isProcessing = false;
          this.bsModalRef.hide();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
